import { Component, OnDestroy, OnInit } from "@angular/core";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import {
  faChevronLeft,
  faChevronRight,
  faGear,
  faGripLines,
  faInfoCircle,
  faPlus,
  faSync
} from "@fortawesome/pro-solid-svg-icons";
import { Dialog } from "@angular/cdk/dialog";
import { RiskAppetite } from "../../../models/risk-appetite.model";
import { Marketplace } from "../../../models/marketplace.model";
import { MarketplaceService } from "../../../services/marketplace.service";
import { CdkConnectedOverlay, CdkOverlayOrigin, OverlayModule } from "@angular/cdk/overlay";
import { RouterLink } from "@angular/router";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { HttpErrorResponse } from "@angular/common/http";
import { StateManagementService } from "../../../services/state-management.service";
import { NgClass } from "@angular/common";
import { ToastService } from "../../../services/toast.service";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray
} from "@angular/cdk/drag-drop";
import { MarketplacePriority } from "../../../models/marketplace-priority.model";
import { forkJoin, Observable, of, Subject, switchMap, takeUntil, tap } from "rxjs";
import { AgencyService } from "../../../services/agency.service";
import { Page } from "../../../models/page.model";
import { Agency } from "../../../models/agency.model";
import { CanDeactivateComponent } from "../../../guards/unsaved-changes.guard";
import { faEllipsisV } from "@fortawesome/pro-light-svg-icons";
import { MarketplaceDeliveryStats } from "../../../models/marketplace-delivery-stats.model";
import { MarketplaceDeliveryError } from "../../../models/marketplace-delivery-error.model";
import {
  MarketplaceChangelogModalComponent,
  MarketplaceChangelogModalData
} from "./marketplace-changelog-modal/marketplace-changelog-modal.component";
import { InfoModalButtonComponent } from "../../info-modal/info-modal-button.component";
import { InfoModalBullet, InfoModalData } from "../../info-modal/info-modal.model";
import { CustomCdkTooltipDirective } from "../../../directives/custom-cdk-tool-tip.directive";

/**
 * PURPOSE: responsible for retrieving and displaying all marketplaces
 */
@Component({
  selector: "app-marketplaces",
  standalone: true,
  imports: [
    RouterLink,
    FaIconComponent,
    NgClass,
    CdkDrag,
    CdkDropList,
    CdkDragPreview,
    CdkDragHandle,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    OverlayModule,
    InfoModalButtonComponent,
    CustomCdkTooltipDirective
  ],
  templateUrl: "./marketplaces.component.html"
})
export class MarketplacesComponent implements OnInit, OnDestroy, CanDeactivateComponent {
  private readonly DELIVERED_PRICE_COLUMN_VALUE_MARKETPLACE: string = "Marketplace";
  private readonly PREPAID_BALANCE_COLUMN_VALUE_BILLED: string = "Billed";
  private readonly PREPAID_BALANCE_AND_DAILY_LIMIT_EXCLUDED_MARKETPLACES: Set<string> = new Set([
    "MARKETPLACE",
    "CONSUMER_FUNNEL"
  ]);
  private readonly LOW_USAGE_THRESHOLD_RATIO: number = 0.15;
  private readonly TOAST_DURATION: number = 7000;
  private onDestroy$: Subject<void> = new Subject<void>();
  private initialMarketplaces: Marketplace[] = [];
  private initialMarketplaceIdOrder: number[] = [];
  private actionsMenuOpenId?: number;
  protected readonly faPlus: IconDefinition = faPlus;
  protected readonly faGear: IconDefinition = faGear;
  protected readonly faGripLines: IconDefinition = faGripLines;
  protected readonly faEllipsisV: IconDefinition = faEllipsisV;
  protected readonly faSync: IconDefinition = faSync;
  protected readonly faChevronLeft: IconDefinition = faChevronLeft;
  protected readonly faChevronRight: IconDefinition = faChevronRight;
  protected readonly faInfoCircle: IconDefinition = faInfoCircle;
  protected readonly Math: Math = Math;

  protected readonly allMarketplacesInfo: InfoModalData = {
    title: "All Marketplaces",
    subtitle:
      "This table lists every marketplace that participates in the standard referral routing flow.",
    sections: [
      {
        heading: "What is this page?",
        items: [
          {
            label: "Purpose",
            description:
              "This is where admins manage the marketplaces that receive referrals from the platform.",
            bullets: [
              "Use it to control which partner marketplaces are active and receiving referrals.",
              "Set the priority order that determines which marketplace is tried first.",
              "Enable or disable individual marketplaces without deleting them.",
              "Monitor recent delivery activity at a glance."
            ]
          },
          {
            label: "Who uses it?",
            description: "Users with the ROLE_CONSOLE_ADMIN role."
          }
        ]
      },
      {
        heading: "Table Columns",
        items: [
          {
            label: "Marketplace",
            description:
              "The name of the marketplace. If the marketplace is linked to an agency, the agency's city, state, and ID are shown beneath the name."
          },
          {
            label: "Actions",
            description: "A menu (⋮) with options for managing the marketplace.",
            bullets: [
              "Edit Marketplace Appetite — opens the full settings page for that marketplace.",
              "View Changelog — shows a complete history of every configuration change made to that marketplace.",
              "View Referral Activity — coming soon."
            ]
          },
          {
            label: "Enabled",
            description: "A toggle to turn this marketplace on or off.",
            bullets: [
              "When enabled (purple), the marketplace is active and will receive referrals according to its priority.",
              "When disabled (gray), the marketplace is excluded from referral routing until re-enabled.",
              "Disabling a marketplace moves it to the bottom of the list. It must be re-enabled and saved to rejoin the priority order."
            ]
          },
          {
            label: "Delivered Price",
            description: "The pricing configuration for referrals sent to this marketplace.",
            bullets: [
              "Dollar amount — a fixed price is charged per delivered referral.",
              "Percentage (e.g. +5% or -10%) — a modifier is applied to the base marketplace price.",
              '"Marketplace" — no price override is set; the default marketplace price applies.'
            ]
          },
          {
            label: "Prepaid Balance",
            description: "Shows the current financial status for this marketplace.",
            bullets: [
              "Dollar amount — the agency's prepaid balance. Turns red when it falls below $500.",
              '"Billed" — this marketplace is invoiced rather than prepaid.',
              "Blank — neither billing nor prepaid balance applies."
            ]
          },
          {
            label: "Last Delivered",
            description:
              "When the most recent referral was successfully sent to this marketplace. The colored dot indicates how recent:",
            bullets: [
              '<span class="text-green-500 font-semibold">Green</span> — delivered today.',
              '<span class="text-yellow-500 font-semibold">Yellow</span> — delivered yesterday.',
              '<span class="text-orange-500 font-semibold">Orange</span> — delivered more than a day ago.',
              '<span class="text-red-500 font-semibold">Red</span> — the last delivery attempt failed. Check the Marketplace Error Activity section below.',
              {
                icon: faSync,
                text: "Click the <strong>sync button</strong> next to the column header to reload delivery stats."
              } as InfoModalBullet
            ]
          },
          {
            label: "Daily Limit",
            description:
              'Shows how many referrals have been delivered to this marketplace today versus its daily maximum (e.g. "3 / 10"). The colored dot indicates status:',
            bullets: [
              '<span class="text-green-500 font-semibold">Green</span> — available, under the limit (or no limit configured).',
              '<span class="text-yellow-500 font-semibold">Yellow</span> — low usage, under 15% of the limit delivered so far.',
              '<span class="text-red-500 font-semibold">Red</span> — the daily limit has been reached; no more referrals will be sent until the next day.',
              'Shows "No Limits" if no daily cap has been configured.'
            ]
          },
          {
            label: "Monthly Limit",
            description:
              'Shows how many referrals have been delivered to this marketplace this calendar month versus its monthly maximum (e.g. "32 / 50"). Uses the same colored-dot status as Daily Limit.',
            bullets: [
              "Resets at the start of each calendar month.",
              'When the monthly limit is reached, a "Next" date shows the start of the next calendar month, when capacity becomes available again.',
              'Shows "No limit" if no monthly cap has been configured.'
            ]
          },
          {
            label: "Categories",
            description: "The insurance lines this marketplace is configured to accept.",
            bullets: [
              "PL — Personal Lines (e.g. auto, home, renters).",
              "CL — Commercial Lines (e.g. business, liability).",
              "L&A — Life & Annuities.",
              "A marketplace may accept one or more categories."
            ]
          }
        ]
      },
      {
        heading: "Reordering Marketplaces",
        items: [
          {
            label: "How priority works",
            description:
              "Enabled marketplaces are evaluated in the order they appear in this list — top to bottom. The first marketplace that matches a referral's criteria will receive it."
          },
          {
            label: "How to reorder",
            description:
              "Use the grip icon on the left side of each row to drag and reposition enabled marketplaces.",
            bullets: [
              {
                icon: faGripLines,
                text: "Drag any enabled marketplace row to a new position."
              } as InfoModalBullet,
              "Only enabled marketplaces can be reordered.",
              "Disabled marketplaces appear at the bottom and cannot be dragged.",
              "After reordering, click Save Updates to apply the new priority order."
            ]
          },
          {
            label: "Marketplace Types",
            description:
              "Not all marketplaces behave the same way. There are four distinct types you may see in this list:",
            bullets: [
              "<strong>CCR — Consumer Choice Referral:</strong> The consumer actively selects which agency they want to contact. Rather than agencies competing to claim the opportunity, the consumer drives the choice. These are first delivered to the <strong>Consumer Funnel</strong> marketplace and if not confirmed moves on to the other marketplaces.",
              "<strong>PCR — Premium Choice Referral:</strong> A priority marketplace where select premium agencies get first access to an opportunity before it becomes available to others. Agencies in a PCR position see the opportunity ahead of the standard queue. Any Marketplace above the Claim-It Marketplace (called MARKETPLACE), except the Consumer Funnel Marketplace, is considered a PCR marketplace.",
              "<strong>CIR — Claim-It Referral:</strong> The standard real-time marketplace (called MARKETPLACE) where agencies can claim referrals that fit their appetite.",
              "<strong>Backstop Marketplace:</strong> A fallback partner that receives an opportunity only after all primary marketplace channels have passed on it."
            ]
          }
        ]
      },
      {
        heading: "Enabling & Disabling",
        items: [
          {
            label: "Toggling a marketplace",
            description:
              "Click the toggle in the Enabled column to enable or disable a marketplace.",
            bullets: [
              "Changes are not saved automatically — you must click <strong>Save Updates</strong>.",
              "Disabling a marketplace removes it from the priority order. When re-enabled, it is added to the bottom of the enabled list.",
              "Use <strong>Show Disabled Marketplaces (toggle above the table)</strong> to view disabled marketplaces alongside active ones."
            ]
          }
        ]
      },
      {
        heading: "Saving Changes",
        items: [
          {
            label: "Save Updates",
            description:
              "Commits all unsaved changes — both priority reordering and enable/disable toggles. The button is inactive when there are no pending changes."
          },
          {
            label: "Discard Changes",
            description:
              "Reverts the table to the last saved state, undoing any reordering or toggling you have done since the last save."
          }
        ]
      }
    ]
  };

  protected readonly errorActivityInfo: InfoModalData = {
    title: "Marketplace Error Activity",
    subtitle:
      "A log of referrals that could not be delivered or were rejected by a partner marketplace.",
    sections: [
      {
        heading: "What is this section?",
        items: [
          {
            label: "Purpose",
            description: "This table shows a history of delivery problems across all marketplaces.",
            bullets: [
              "Use it to investigate why a specific referral did not reach a partner.",
              "Identify patterns — for example, a marketplace that repeatedly fails.",
              "Understand what happened to a referral after the error (where it ended up and its current status)."
            ]
          }
        ]
      },
      {
        heading: "Error Types",
        items: [
          {
            label: "Delivery Error",
            description: "The system attempted to send the referral but the delivery failed.",
            bullets: [
              "Common causes: the partner's API was unreachable, returned an unexpected error, or timed out.",
              "Shown in red in the Error Status column.",
              "Action: check the marketplace's API delivery settings via Actions → Edit Marketplace Appetite."
            ]
          },
          {
            label: "Rejected by Partner",
            description:
              "The referral was delivered successfully but the partner declined to accept it.",
            bullets: [
              "Common causes: the referral falls outside the partner's appetite (e.g. duplicate referral).",
              "Shown in gray in the Error Status column.",
              "Action: review the marketplace's risk appetite settings to ensure they match what the partner can accept."
            ]
          }
        ]
      },
      {
        heading: "Table Columns",
        items: [
          {
            label: "Date",
            description: "The date and time when the error occurred."
          },
          {
            label: "Opportunity ID",
            description:
              "The unique identifier for the referral that encountered the issue. Use this ID to look up the referral in other parts of the platform."
          },
          {
            label: "Error Status",
            description: "Whether the issue was a Delivery Error or a Rejected by Partner outcome."
          },
          {
            label: "Error Marketplace",
            description: "The name of the marketplace where the error occurred."
          },
          {
            label: "Error Notes",
            description:
              "Additional detail about what went wrong — often includes the partner\'s error message or rejection reason. Long notes are truncated; click <strong>See more</strong> to expand."
          },
          {
            label: "Current Marketplace",
            description:
              "The marketplace the referral was routed to after the error, if applicable."
          },
          {
            label: "Current Status",
            description: "The referral's status in the current marketplace."
          }
        ]
      },
      {
        heading: "Filtering & Pagination",
        items: [
          {
            label: "Show only delivery errors",
            description:
              "Check this box to hide Rejected by Partner rows and focus exclusively on true delivery failures."
          },
          {
            label: "Pagination",
            description: "Use the Previous and Next buttons to move between pages of results.",
            bullets: [
              "Use the Rows per page dropdown to display 10, 25, 50, or 100 rows at a time.",
              "The record count above the pagination controls shows how many total errors exist."
            ]
          }
        ]
      }
    ]
  };

  protected readonly nonFlowInfo: InfoModalData = {
    title: "Non Flow Marketplaces",
    subtitle: "Marketplaces that operate outside the standard automatic referral routing sequence.",
    sections: [
      {
        heading: "What is a Non Flow Marketplace?",
        items: [
          {
            label: "Standard (flow) marketplaces",
            description:
              "These are evaluated automatically in priority order every time a referral comes in. The system works through the list until it finds a marketplace that matches the referral's criteria."
          },
          {
            label: "Non-flow marketplaces",
            description: "These are excluded from the automatic routing process.",
            bullets: [
              "They are only invoked by specific integrations or used as backstop/fallback partners.",
              "They do not appear in the main priority table and cannot be reordered.",
              "They maybe still active and receiving referrals — just through a dedicated route, not the standard queue or they maybe disabled and do not receive any referrals."
            ]
          },
          {
            label: "Why does this distinction exist?",
            description:
              "Some partners have dedicated or exclusive routing arrangements. Keeping them separate prevents them from interfering with — or being affected by — the standard marketplace priority order."
          }
        ]
      },
      {
        heading: "Table Columns",
        items: [
          {
            label: "Marketplace",
            description:
              "The name of the marketplace. If linked to an agency, the agency's city, state, and ID are shown beneath the name."
          },
          {
            label: "Last Delivered",
            description:
              "When the most recent opportunity was successfully sent to this marketplace. The colored dot indicates how recent:",
            bullets: [
              '<span class="text-green-500 font-semibold">Green</span> — delivered today.',
              '<span class="text-yellow-500 font-semibold">Yellow</span> — delivered yesterday.',
              '<span class="text-orange-500 font-semibold">Orange</span> — delivered more than a day ago.',
              '<span class="text-red-500 font-semibold">Red</span> — the last delivery attempt failed.',
              {
                icon: faSync,
                text: "Click the <strong>sync button</strong> next to the column header to reload delivery stats."
              } as InfoModalBullet
            ]
          },
          {
            label: "Daily Count",
            description: "How many referrals have been delivered to this marketplace today."
          },
          {
            label: "Creation Date",
            description: "The date this marketplace record was created in the system."
          }
        ]
      },
      {
        heading: "Managing Non Flow Marketplaces",
        items: [
          {
            label: "Can I edit a non-flow marketplace?",
            description:
              "Non-flow marketplaces are not directly editable from this view. To change a non-flow marketplace's settings, contact the development team."
          },
          {
            label: "Can I reorder or enable/disable them?",
            description:
              "No — non-flow marketplaces do not participate in the priority order and cannot be toggled from this page."
          }
        ]
      }
    ]
  };

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected isLoadingDeliveryStats: boolean = true;
  protected isLoadingDeliveryErrors: boolean = true;
  protected marketplaces: Marketplace[] = [];
  protected flowMarketplaces: Marketplace[] = [];
  protected enabledFlowMarketplaces: Marketplace[] = [];
  protected disabledFlowMarketplaces: Marketplace[] = [];
  protected nonFlowMarketplaces: Marketplace[] = [];
  protected marketplaceDeliveryStats: MarketplaceDeliveryStats[] = [];
  protected marketplaceDeliveryStatsMap: Map<number, MarketplaceDeliveryStats> = new Map();
  protected deliveryErrors: MarketplaceDeliveryError[] = [];
  protected currentDeliveryErrorsPage: number = 1;
  protected deliveryErrorsPageSize: number = 10;
  protected readonly pageSizeOptions: number[] = [10, 25, 50, 100];
  protected totalDeliveryErrors: number = 0;
  protected totalDeliveryErrorsPages: number = 0;
  protected showOnlyDeliveryErrors: boolean = false;
  protected expandedNotes: Set<number> = new Set();
  protected agencyMap: Map<number, Agency> = new Map();
  protected agencyPrepaidBalanceMap: Map<number, number> = new Map();
  protected hasUnsavedChanges: boolean = false;
  protected currentOverlayOrigin?: CdkOverlayOrigin;
  protected showDisabledMarketplaces: boolean = false;

  constructor(
    private readonly marketplaceService: MarketplaceService,
    private stateManagementService: StateManagementService,
    private toastService: ToastService,
    private agencyService: AgencyService,
    private dialog: Dialog
  ) {}

  ngOnInit(): void {
    this.stateManagementService.setPageTitle("All Marketplaces | Momentum Edge");
    this.stateManagementService.pageHeading.set("All Marketplaces");

    // First load marketplaces - this will allow the table to display quickly
    this.marketplaceService
      .getMarketplaces()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (marketplaces: Marketplace[]): void => {
          this.marketplaces = [...marketplaces];
          this.initialMarketplaces = marketplaces.map(
            (marketplace: Marketplace): Marketplace => ({ ...marketplace })
          );

          // Split marketplaces into flow and non-flow
          this.flowMarketplaces = this.marketplaces.filter(
            (m: Marketplace): boolean => m.includedInMarketplaceFlow
          );
          this.nonFlowMarketplaces = this.marketplaces.filter(
            (m: Marketplace): boolean => !m.includedInMarketplaceFlow
          );

          // Split flow marketplaces into enabled and disabled
          this.enabledFlowMarketplaces = this.flowMarketplaces
            .filter((m: Marketplace): boolean => m.enabled)
            .sort(
              (a: Marketplace, b: Marketplace): number =>
                (a.priority ?? Infinity) - (b.priority ?? Infinity)
            );
          this.disabledFlowMarketplaces = this.flowMarketplaces
            .filter((m: Marketplace): boolean => !m.enabled)
            .sort((a: Marketplace, b: Marketplace): number =>
              a.marketplaceName!.localeCompare(b.marketplaceName!)
            );

          // Sort non-flow marketplaces alphabetically
          this.nonFlowMarketplaces.sort((a: Marketplace, b: Marketplace): number =>
            a.marketplaceName!.localeCompare(b.marketplaceName!)
          );

          // Set initial order for checking changes (only for flow marketplaces)
          this.initialMarketplaceIdOrder = this.enabledFlowMarketplaces.map(
            (marketplace: Marketplace): number => marketplace.id!
          );

          // Load agencies data
          this.loadAgenciesForMarketplaces()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (): void => {
                this.isLoading = false;
              },
              error: (error: HttpErrorResponse): void => {
                console.error("Error loading agencies data:", error);
              }
            });

          // Load delivery stats separately so as not to block the UI from displaying
          this.loadDeliveryStatsForMarketplaces()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (): void => {
                this.isLoadingDeliveryStats = false;
              },
              error: (error: HttpErrorResponse): void => {
                this.isLoadingDeliveryStats = false;
                this.toastService.open(
                  "Failed to retrieve some delivery statistics information. Please refresh the page and try again.",
                  "Close",
                  {
                    duration: this.TOAST_DURATION,
                    panelClass: ["bg-red-600"]
                  }
                );
                console.error("Error loading delivery stats:", error);
              }
            });

          // Load delivery errors
          this.loadDeliveryErrors();
        },
        error: (error: HttpErrorResponse): void => {
          console.error("Error loading marketplaces:", error);
          this.isLoading = false;
        }
      });
  }

  canDeactivate(): boolean {
    return !this.hasUnsavedChanges;
  }

  private loadAgenciesForMarketplaces(): Observable<unknown> {
    // Get unique agency IDs from marketplaces
    const agencyIds: number[] = [
      ...new Set(
        this.marketplaces
          .filter((marketplace: Marketplace): number | undefined => marketplace.agencyId)
          .map((marketplace: Marketplace): number => marketplace.agencyId!)
      )
    ];

    if (agencyIds.length === 0) {
      return of([]);
    }

    // Create observables for each agency request
    const agencyRequests: Observable<Page<Agency>>[] = agencyIds.map(
      (agencyId: number): Observable<Page<Agency>> =>
        this.agencyService.getAgency(agencyId).pipe(takeUntil(this.onDestroy$))
    );

    // Execute all agency requests in parallel, then chain to prepaid balances
    return forkJoin(agencyRequests).pipe(
      takeUntil(this.onDestroy$),
      tap({
        next: (agencyPages: Page<Agency>[]): void => {
          agencyPages.forEach((agencyPage: Page<Agency>, index: number): void => {
            if (agencyPage.content.length > 0) {
              const agency: Agency = agencyPage.content[0];
              this.agencyMap.set(agencyIds[index], agency);
            }
          });
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            "Failed to retrieve some agency information. Please refresh the page and try again.",
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
          this.isLoading = false;
        }
      }),
      // Chain to load prepaid balances
      switchMap((): Observable<number[]> => this.loadPrepaidBalancesForAgencies())
    );
  }

  private loadPrepaidBalancesForAgencies(): Observable<number[]> {
    // Get unique agency IDs that have debitPrepaidBalance set to true
    const agencyIdsForPrepaidBalance: number[] = [
      ...new Set(
        this.marketplaces
          .filter(
            (marketplace: Marketplace): boolean =>
              marketplace.agencyId !== undefined && marketplace.debitPrepaidBalance
          )
          .map((marketplace: Marketplace): number => marketplace.agencyId!)
      )
    ];

    if (agencyIdsForPrepaidBalance.length === 0) {
      return of([]);
    }

    // Create observables for each prepaid balance request
    const prepaidBalanceRequests: Observable<number>[] = agencyIdsForPrepaidBalance.map(
      (agencyId: number): Observable<number> =>
        this.agencyService.getAgencyPrepaidBalance(agencyId).pipe(takeUntil(this.onDestroy$))
    );

    // Execute all prepaid balance requests in parallel and return the observable
    return forkJoin(prepaidBalanceRequests).pipe(
      takeUntil(this.onDestroy$),
      tap({
        next: (prepaidBalances: number[]): void => {
          prepaidBalances.forEach((balance: number, index: number): void => {
            if (balance !== undefined && balance !== null) {
              this.agencyPrepaidBalanceMap.set(agencyIdsForPrepaidBalance[index], balance);
            }
          });
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            "Failed to retrieve some prepaid balance information. Please ensure the API service is running and refresh the page and try again.",
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
          this.isLoading = false;
        }
      })
    );
  }

  private loadDeliveryStatsForMarketplaces(): Observable<MarketplaceDeliveryStats[]> {
    return this.marketplaceService.getDeliveryStatsForMarketplaces().pipe(
      takeUntil(this.onDestroy$),
      tap((deliveryStats: MarketplaceDeliveryStats[]): void => {
        this.marketplaceDeliveryStats = deliveryStats;

        // Create a map for quick lookup by marketplace ID
        this.marketplaceDeliveryStatsMap.clear();
        deliveryStats.forEach((stats: MarketplaceDeliveryStats): void => {
          if (stats.marketplaceId) {
            this.marketplaceDeliveryStatsMap.set(stats.marketplaceId, stats);
          }
        });
      })
    );
  }

  private loadDeliveryErrors(): void {
    this.isLoadingDeliveryErrors = true;

    this.marketplaceService
      .getDeliveryErrorsForMarketplaces(
        this.currentDeliveryErrorsPage - 1,
        this.deliveryErrorsPageSize,
        this.showOnlyDeliveryErrors
      )
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (deliveryErrorsPage: Page<MarketplaceDeliveryError>): void => {
          this.deliveryErrors = deliveryErrorsPage.content;
          this.totalDeliveryErrors = deliveryErrorsPage.page.totalElements;
          this.totalDeliveryErrorsPages = deliveryErrorsPage.page.totalPages;
          this.isLoadingDeliveryErrors = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.isLoadingDeliveryErrors = false;
          this.toastService.open(
            "Failed to retrieve delivery errors. Please refresh the page and try again.",
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  protected getAgencyForMarketplace(marketplace: Marketplace): Agency | undefined {
    return marketplace.agencyId ? this.agencyMap.get(marketplace.agencyId) : undefined;
  }

  protected getFormattedPrepaidBalance(marketplace: Marketplace): string {
    // Marketplace has agencyId and marketplace is set to debitPrepaidBalance
    if (marketplace.agencyId && marketplace.debitPrepaidBalance) {
      const balance: number | undefined = this.agencyPrepaidBalanceMap.get(marketplace.agencyId);
      if (balance !== undefined && balance !== null) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD"
        }).format(balance);
      }
      return "$0.00";
    }

    // Marketplace is set to billed
    if (marketplace.billed) {
      if (
        this.PREPAID_BALANCE_AND_DAILY_LIMIT_EXCLUDED_MARKETPLACES.has(marketplace.marketplaceName!)
      ) {
        return "";
      }
      return this.PREPAID_BALANCE_COLUMN_VALUE_BILLED;
    }

    return "";
  }

  protected getPrepaidBalanceClass(marketplace: Marketplace): string {
    // If marketplace has agencyId and debitPrepaidBalance is true
    if (marketplace.agencyId && marketplace.debitPrepaidBalance) {
      const balance: number | undefined = this.agencyPrepaidBalanceMap.get(marketplace.agencyId);
      if (balance !== undefined && balance !== null && balance < 500) {
        return "text-red-600";
      }
    }
    return "text-agentium_gray-text";
  }

  protected openActionsMenu(rowId: number | undefined, cdkOverlayOrigin: CdkOverlayOrigin): void {
    this.actionsMenuOpenId = rowId;
    this.currentOverlayOrigin = cdkOverlayOrigin;
  }

  protected closeActionsMenu(): void {
    this.actionsMenuOpenId = undefined;
    this.currentOverlayOrigin = undefined;
  }

  protected isActionsMenuOpen(rowId: number | undefined): boolean {
    return this.actionsMenuOpenId === rowId;
  }

  protected openChangelogModal(marketplace: Marketplace): void {
    this.closeActionsMenu();
    this.dialog.open<void, MarketplaceChangelogModalData>(MarketplaceChangelogModalComponent, {
      data: {
        marketplaceId: marketplace.id!,
        marketplaceName: marketplace.marketplaceName!
      },
      panelClass: ["flex", "items-center", "justify-center"]
    });
  }

  protected get filteredFlowMarketplaces(): Marketplace[] {
    if (this.showDisabledMarketplaces) {
      return [...this.enabledFlowMarketplaces, ...this.disabledFlowMarketplaces];
    }
    return this.enabledFlowMarketplaces;
  }

  protected toggleMarketplaceEnabled(marketplace: Marketplace): void {
    marketplace.enabled = !marketplace.enabled;

    if (marketplace.enabled) {
      // Marketplace is being enabled, remove it from the disabled list and add to the end of the enabled list
      const marketplaceIndex: number = this.disabledFlowMarketplaces.findIndex(
        (m: Marketplace): boolean => m.id === marketplace.id
      );
      if (marketplaceIndex > -1) {
        const [movedMarketplace] = this.disabledFlowMarketplaces.splice(marketplaceIndex, 1);
        this.enabledFlowMarketplaces.push(movedMarketplace);
      }
    } else {
      // Marketplace is being disabled remove it from the enabled list and add to the disabled list, sorted alphabetically
      const marketplaceIndex: number = this.enabledFlowMarketplaces.findIndex(
        (m: Marketplace): boolean => m.id === marketplace.id
      );
      if (marketplaceIndex > -1) {
        const [movedMarketplace] = this.enabledFlowMarketplaces.splice(marketplaceIndex, 1);
        movedMarketplace.priority = undefined;
        this.disabledFlowMarketplaces.push(movedMarketplace);
        this.disabledFlowMarketplaces.sort((a: Marketplace, b: Marketplace): number =>
          a.marketplaceName!.localeCompare(b.marketplaceName!)
        );
      }
    }

    this.checkForChanges();
  }

  protected getFormattedDeliveredPrice(marketplace: Marketplace): string {
    if (marketplace.applyDeliveredPrice) {
      // Display as currency
      if (marketplace.deliveredPrice !== undefined && marketplace.deliveredPrice !== null) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD"
        }).format(marketplace.deliveredPrice);
      }
      return "$0.00";
    } else {
      // Check priceModifier value
      const priceModifier: number = this.convertPriceModifierToPercentModifier(
        marketplace.priceModifier
      );
      if (priceModifier === 0 || priceModifier === undefined) {
        return this.DELIVERED_PRICE_COLUMN_VALUE_MARKETPLACE;
      } else {
        // Display as percentage with sign (negative values has a sign by default so accounting for positive values)
        const sign: string = priceModifier > 0 ? "+" : "";
        return `${sign}${priceModifier}%`;
      }
    }
  }

  /**
   * Converts a price modifier to a percentage modifier
   * @param priceModifier - The price modifier value
   * @returns The percentage modifier value
   */
  private convertPriceModifierToPercentModifier(priceModifier?: number): number {
    if (priceModifier == undefined) return 0;
    return Number(((priceModifier - 1) * 100).toFixed(0));
  }

  protected getCategory(riskAppetite: RiskAppetite): string {
    let categories: string[] = [];
    if (riskAppetite?.acceptPersonalLines) categories.push("PL");
    if (riskAppetite?.acceptCommercialLines) categories.push("CL");
    if (riskAppetite?.acceptLifeAnnuitiesLines) categories.push("L&A");
    return categories.join(", ");
  }

  /**
   * Formats the Last Delivered column data with appropriate status indicators
   * @param marketplace The marketplace to get the last delivered date for
   * @returns Formatted string with status indicator
   */
  protected getFormattedLastDelivered(marketplace: Marketplace): string {
    if (this.isLoadingDeliveryStats) {
      return '<span class="inline-block animate-pulse">Loading...</span>';
    }

    if (!marketplace.id) {
      return (
        '<div class="flex items-center">' +
        '<span class="inline-block w-2 h-2 rounded-full bg-gray-400 mr-1"></span> > 1 Month' +
        "</div>"
      );
    }

    const deliveryStats: MarketplaceDeliveryStats | undefined =
      this.marketplaceDeliveryStatsMap.get(marketplace.id);

    if (!deliveryStats || !deliveryStats.lastReferralDeliverySuccess) {
      return (
        '<div class="flex items-center">' +
        '<span class="inline-block w-2 h-2 rounded-full bg-red-500 mr-1"></span> Delivery Error' +
        "</div>"
      );
    }

    if (!deliveryStats || !deliveryStats.lastReferralDeliveryDate) {
      return (
        '<div class="flex items-center">' +
        '<span class="inline-block w-2 h-2 rounded-full bg-gray-400 mr-1"></span> > 1 Month' +
        "</div>"
      );
    }

    const lastDeliveryDate = new Date(deliveryStats.lastReferralDeliveryDate);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Format time as HH:MM AM/PM
    const hours: number = lastDeliveryDate.getHours();
    const minutes: number = lastDeliveryDate.getMinutes();
    const ampm: string = hours >= 12 ? "PM" : "AM";
    const formattedHours: number = hours % 12 || 12;
    const formattedMinutes: string | number = minutes < 10 ? "0" + minutes : minutes;
    const timeString = `${formattedHours}:${formattedMinutes}${ampm}`;

    // Check if the date is today
    if (lastDeliveryDate.setHours(0, 0, 0, 0) === today.setHours(0, 0, 0, 0)) {
      return (
        '<div class="flex items-center">' +
        '<span class="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 flex-shrink-0"></span>' +
        '<div class="flex flex-col">' +
        "<span>Today</span>" +
        "<span>" +
        timeString +
        "</span>" +
        "</div>" +
        "</div>"
      );
    }

    // Check if the date is yesterday
    if (lastDeliveryDate.setHours(0, 0, 0, 0) === yesterday.setHours(0, 0, 0, 0)) {
      return (
        '<div class="flex items-center">' +
        '<span class="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-2 flex-shrink-0"></span>' +
        '<div class="flex flex-col">' +
        "<span>Yesterday</span>" +
        "<span>" +
        timeString +
        "</span>" +
        "</div>" +
        "</div>"
      );
    }

    // Date is older than yesterday
    const month: number = lastDeliveryDate.getMonth() + 1;
    const day: number = lastDeliveryDate.getDate();
    const year: number = lastDeliveryDate.getFullYear();
    const dateString = `${month}/${day}/${year.toString().slice(2)}`;

    return (
      '<div class="flex items-center">' +
      '<span class="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2 flex-shrink-0"></span>' +
      '<div class="flex flex-col">' +
      "<span>" +
      dateString +
      "</span>" +
      "<span>" +
      timeString +
      "</span>" +
      "</div>" +
      "</div>"
    );
  }

  /**
   * Builds the colored status dot shared by the Daily Limit and Monthly Limit columns.
   * Red once the limit is reached, yellow while usage is under the low-usage threshold, green otherwise.
   * @param delivered The number of referrals delivered in the current period
   * @param limit The configured maximum for that period
   * @returns HTML for the status dot span
   */
  private getLimitStatusDot(delivered: number, limit: number): string {
    let dotColorClass: string;
    if (delivered >= limit) {
      dotColorClass = "bg-red-500";
    } else if (delivered < limit * this.LOW_USAGE_THRESHOLD_RATIO) {
      dotColorClass = "bg-yellow-500";
    } else {
      dotColorClass = "bg-green-500";
    }
    return `<span class="inline-block w-2 h-2 rounded-full ${dotColorClass} mr-1"></span>`;
  }

  /**
   * Formats an ISO date string as e.g. "Jan 17, 2026" for the Monthly Limit "Next" line.
   * @param isoDate The ISO date-time string to format
   * @returns The formatted date
   */
  private formatMonthlyResetDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  }

  /**
   * Formats the Daily Limit column data with appropriate styling
   * @param marketplace The marketplace to get the daily limit for
   * @returns Formatted string with daily limit information
   */
  protected getFormattedDailyLimit(marketplace: Marketplace): string {
    // If marketplace name is in excluded list, return empty string
    if (
      marketplace.marketplaceName &&
      this.PREPAID_BALANCE_AND_DAILY_LIMIT_EXCLUDED_MARKETPLACES.has(marketplace.marketplaceName)
    ) {
      return "";
    }

    // Show the loading indicator if delivery stats are still loading
    if (this.isLoadingDeliveryStats) {
      return '<span class="inline-block animate-pulse">Loading...</span>';
    }

    // Check if a marketplace has risk appetite with maxReferralsPerDay
    if (!marketplace.riskAppetite?.maxReferralsPerDay) {
      return '<span class="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>No Limits';
    }

    const maxReferralsPerDay: number = marketplace.riskAppetite.maxReferralsPerDay;

    // Get total referrals delivered today from stats
    let totalReferralsDeliveredToday: number = 0;
    if (marketplace.id) {
      const stats: MarketplaceDeliveryStats | undefined = this.marketplaceDeliveryStatsMap.get(
        marketplace.id
      );
      if (stats && stats.totalReferralsDeliveredToday !== undefined) {
        totalReferralsDeliveredToday = stats.totalReferralsDeliveredToday;
      }
    }

    const formattedValue = `${totalReferralsDeliveredToday} / ${maxReferralsPerDay}`;
    return (
      this.getLimitStatusDot(totalReferralsDeliveredToday, maxReferralsPerDay) + formattedValue
    );
  }

  /**
   * Formats the Monthly Limit column data with appropriate styling
   * @param marketplace The marketplace to get the monthly limit for
   * @returns Formatted string with monthly limit information
   */
  protected getFormattedMonthlyLimit(marketplace: Marketplace): string {
    // If marketplace name is in excluded list, return empty string
    if (
      marketplace.marketplaceName &&
      this.PREPAID_BALANCE_AND_DAILY_LIMIT_EXCLUDED_MARKETPLACES.has(marketplace.marketplaceName)
    ) {
      return "";
    }

    // Show the loading indicator if delivery stats are still loading
    if (this.isLoadingDeliveryStats) {
      return '<span class="inline-block animate-pulse">Loading...</span>';
    }

    // Check if a marketplace has risk appetite with maxReferralsPerMonth
    if (!marketplace.riskAppetite?.maxReferralsPerMonth) {
      return '<span class="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>No limit';
    }

    const maxReferralsPerMonth: number = marketplace.riskAppetite.maxReferralsPerMonth;

    // Get total referrals delivered this month, and the next eligible date, from stats
    let totalReferralsDeliveredThisMonth: number = 0;
    let nextEligibleDate: string | undefined;
    if (marketplace.id) {
      const stats: MarketplaceDeliveryStats | undefined = this.marketplaceDeliveryStatsMap.get(
        marketplace.id
      );
      if (stats && stats.totalReferralsDeliveredThisMonth !== undefined) {
        totalReferralsDeliveredThisMonth = stats.totalReferralsDeliveredThisMonth;
      }
      nextEligibleDate = stats?.nextEligibleDate;
    }

    const formattedValue = `${totalReferralsDeliveredThisMonth} / ${maxReferralsPerMonth}`;
    const dot = this.getLimitStatusDot(totalReferralsDeliveredThisMonth, maxReferralsPerMonth);

    if (totalReferralsDeliveredThisMonth >= maxReferralsPerMonth && nextEligibleDate) {
      return (
        '<div class="flex flex-col">' +
        `<span>${dot}${formattedValue}</span>` +
        `<span class="text-gray-400 text-xs">Next: ${this.formatMonthlyResetDate(nextEligibleDate)}</span>` +
        "</div>"
      );
    }

    return dot + formattedValue;
  }

  /**
   * Formats the Daily Count column data with appropriate styling
   * @param marketplace The marketplace to get the daily count for
   * @returns Formatted string with daily count information
   */
  protected getFormattedDailyCount(marketplace: Marketplace): string {
    // Show the loading indicator if delivery stats are still loading
    if (this.isLoadingDeliveryStats) {
      return '<span class="inline-block animate-pulse">Loading...</span>';
    }

    // Get total referrals delivered today from stats
    let totalReferralsDeliveredToday: number = 0;
    if (marketplace.id) {
      const stats: MarketplaceDeliveryStats | undefined = this.marketplaceDeliveryStatsMap.get(
        marketplace.id
      );
      if (stats && stats.totalReferralsDeliveredToday !== undefined) {
        totalReferralsDeliveredToday = stats.totalReferralsDeliveredToday;
      }
    }

    return `${totalReferralsDeliveredToday}`;
  }

  /**
   * Refreshes the delivery stats data
   * @param event The click event
   */
  protected refreshDeliveryStats(event: MouseEvent): void {
    // Prevent event propagation to avoid sorting or other actions
    event.preventDefault();
    event.stopPropagation();

    this.isLoadingDeliveryStats = true;

    // Fetch fresh data
    this.loadDeliveryStatsForMarketplaces()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (): void => {
          this.isLoadingDeliveryStats = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.isLoadingDeliveryStats = false;
          console.error("Error refreshing delivery stats:", error);
          this.toastService.open(
            "Failed to refresh delivery statistics. Please try again.",
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  private checkForChanges(): void {
    // Check for marketplace order changes in the enabled list (only flow marketplaces)
    const currentMarketplaceIdOrder: number[] = this.enabledFlowMarketplaces.map(
      (marketplace: Marketplace): number => marketplace.id!
    );
    const marketplaceIdOrderChanged: boolean = !this.arraysEqual(
      currentMarketplaceIdOrder,
      this.initialMarketplaceIdOrder
    );

    // Check for enabled state changes by comparing the current state with the initial state (only flow marketplaces)
    const currentFlowMarketplaceStates = new Map([
      ...this.enabledFlowMarketplaces.map(
        (m: Marketplace): [number, boolean] => [m.id, true] as [number, boolean]
      ),
      ...this.disabledFlowMarketplaces.map(
        (m: Marketplace): [number, boolean] => [m.id, false] as [number, boolean]
      )
    ]);
    const marketplaceEnabledStateChanged: boolean = this.initialMarketplaces
      .filter((m: Marketplace): boolean => m.includedInMarketplaceFlow)
      .some((initialMarketplace: Marketplace): boolean => {
        const currentState: boolean | undefined = currentFlowMarketplaceStates.get(
          initialMarketplace.id!
        );
        return currentState !== undefined && currentState !== initialMarketplace.enabled;
      });

    this.hasUnsavedChanges = marketplaceIdOrderChanged || marketplaceEnabledStateChanged;
  }

  private arraysEqual(array1: (string | number)[], array2: (string | number)[]): boolean {
    if (array1.length !== array2.length) return false;
    return array1.every((val: string | number, index: number): boolean => val === array2[index]);
  }

  protected updateMarketplaces(): void {
    this.isSaving = true;

    // Combine only flow marketplaces for the update
    const flowMarketplacesToUpdate: Marketplace[] = [
      ...this.enabledFlowMarketplaces,
      ...this.disabledFlowMarketplaces
    ];

    // Identify marketplaces with changed enabled state (only flow marketplaces)
    const marketplacesWithEnabledStateChanged: Marketplace[] = flowMarketplacesToUpdate.filter(
      (changedMarketplace: Marketplace): boolean => {
        const initialMarketplace: Marketplace | undefined = this.initialMarketplaces.find(
          (initialMarketplace: Marketplace): boolean =>
            initialMarketplace.id === changedMarketplace.id
        );
        return initialMarketplace
          ? changedMarketplace.enabled !== initialMarketplace.enabled
          : false;
      }
    );

    // Identify marketplaces with changed priority (only flow marketplaces)
    const currentMarketplaceIdOrder: number[] = this.enabledFlowMarketplaces.map(
      (marketplace: Marketplace): number => marketplace.id!
    );
    const hasPriorityChanges: boolean = !this.numberArraysEqual(
      currentMarketplaceIdOrder,
      this.initialMarketplaceIdOrder
    );

    // If no changes, return immediately
    if (marketplacesWithEnabledStateChanged.length === 0 && !hasPriorityChanges) {
      this.isSaving = false;
      return;
    }

    // Create a sequential observable chain to process the enabled state and priority changes separately
    let marketplaceUpdateChain$: Observable<any> = of(null);

    // First, update priorities if there are changes
    if (hasPriorityChanges) {
      // Create priority list, but only for enabled flow marketplaces
      const prioritiesToUpdate: MarketplacePriority[] = this.enabledFlowMarketplaces.map(
        (marketplace: Marketplace, index: number): MarketplacePriority => ({
          id: marketplace.id ?? -1,
          priority: index + 1
        })
      );

      marketplaceUpdateChain$ = marketplaceUpdateChain$.pipe(
        switchMap(
          (): Observable<null> =>
            this.marketplaceService.updateMarketplacePriorities(prioritiesToUpdate)
        )
      );
    }

    // Then, update enabled states if there are changes
    if (marketplacesWithEnabledStateChanged.length > 0) {
      marketplaceUpdateChain$ = marketplaceUpdateChain$.pipe(
        switchMap(
          (): Observable<null> =>
            this.marketplaceService.updateMarketplaces(marketplacesWithEnabledStateChanged)
        )
      );
    }

    // Execute the sequential chain
    marketplaceUpdateChain$.pipe(takeUntil(this.onDestroy$)).subscribe({
      next: (): void => {
        this.toastService.open("Marketplaces updated successfully.", "Close", {
          duration: this.TOAST_DURATION,
          panelClass: ["bg-green-600"]
        });

        // Update the initial state to match the current state (only flow marketplaces)
        this.initialMarketplaces = this.marketplaces.map(
          (initialMarketplace: Marketplace): Marketplace => ({
            ...initialMarketplace,
            enabled: initialMarketplace.enabled
          })
        );

        this.initialMarketplaceIdOrder = this.enabledFlowMarketplaces.map(
          (marketplace: Marketplace): number => marketplace.id!
        );
        this.hasUnsavedChanges = false;
      },
      error: (error: HttpErrorResponse): void => {
        this.toastService.open(
          "Failed to save marketplaces. Please refresh the page and try again.",
          "Close",
          {
            duration: this.TOAST_DURATION,
            panelClass: ["bg-red-600"]
          }
        );
      },
      complete: (): void => {
        this.isSaving = false;
      }
    });
  }

  protected discardChanges(): void {
    // Reset the marketplaces arrays to the initial state
    this.marketplaces = this.initialMarketplaces.map(
      (initialMarketplace: Marketplace): Marketplace => ({
        ...initialMarketplace,
        enabled: initialMarketplace.enabled
      })
    );

    // Re-split marketplaces into flow and non-flow
    this.flowMarketplaces = this.marketplaces.filter(
      (m: Marketplace): boolean => m.includedInMarketplaceFlow
    );
    this.nonFlowMarketplaces = this.marketplaces.filter(
      (m: Marketplace): boolean => !m.includedInMarketplaceFlow
    );

    this.enabledFlowMarketplaces = this.flowMarketplaces
      .filter((m: Marketplace): boolean => m.enabled)
      .sort(
        (a: Marketplace, b: Marketplace): number =>
          (a.priority ?? Infinity) - (b.priority ?? Infinity)
      );

    this.disabledFlowMarketplaces = this.flowMarketplaces
      .filter((m: Marketplace): boolean => !m.enabled)
      .sort((a: Marketplace, b: Marketplace): number =>
        a.marketplaceName!.localeCompare(b.marketplaceName!)
      );

    // Sort non-flow marketplaces alphabetically
    this.nonFlowMarketplaces.sort((a: Marketplace, b: Marketplace): number =>
      a.marketplaceName!.localeCompare(b.marketplaceName!)
    );

    this.hasUnsavedChanges = false;
  }

  private numberArraysEqual(array1: number[], array2: number[]): boolean {
    return (
      array1.length === array2.length &&
      array1.every((value: number, index: number): boolean => value === array2[index])
    );
  }

  protected onDragStarted(): void {
    document.body.classList.add("cursor-grabbing");
  }

  protected onDragEnded(): void {
    document.body.classList.remove("cursor-grabbing");
  }

  protected onDrop(event: CdkDragDrop<Marketplace[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      // Update the local enabledFlowMarketplaces array
      moveItemInArray(this.enabledFlowMarketplaces, event.previousIndex, event.currentIndex);

      this.checkForChanges();
    }
  }

  protected nextDeliveryErrorsPage(): void {
    if (this.currentDeliveryErrorsPage < this.totalDeliveryErrorsPages) {
      this.currentDeliveryErrorsPage++;
      this.loadDeliveryErrors();
    }
  }

  protected previousDeliveryErrorsPage(): void {
    if (this.currentDeliveryErrorsPage > 1) {
      this.currentDeliveryErrorsPage--;
      this.loadDeliveryErrors();
    }
  }

  protected onPageSizeChange(size: number): void {
    this.deliveryErrorsPageSize = size;
    this.currentDeliveryErrorsPage = 1;
    this.loadDeliveryErrors();
  }

  protected onDeliveryErrorFilterToggle(newValue: boolean): void {
    this.showOnlyDeliveryErrors = newValue;
    this.currentDeliveryErrorsPage = 1;
    this.loadDeliveryErrors();
  }

  protected toggleNotes(opportunityId: number): void {
    if (this.expandedNotes.has(opportunityId)) {
      this.expandedNotes.delete(opportunityId);
    } else {
      this.expandedNotes.add(opportunityId);
    }
  }

  protected isNotesExpanded(opportunityId: number): boolean {
    return this.expandedNotes.has(opportunityId);
  }

  protected formatDate(dateString: string): string {
    const date: Date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
